import { decodeBase64Url } from "./base64url";
import { createCoreError } from "../errors/coreError.js";
import { normalizeProject } from "../domain/normalize";
import { getHandoverCodec } from "./codecs";

export const HANDOVER_KIND = "handover";
export const HANDOVER_VERSION = 1;
export const DEFAULT_HANDOVER_CODEC = "g9";
export const DEFAULT_HANDOVER_SCHEME = "plaatplan";
export const HANDOVER_IMPORT_PATH = "import";

function isHandoverEnvelope(value) {
  return Boolean(
    value
    && typeof value === "object"
    && value.kind === HANDOVER_KIND
    && typeof value.codec === "string"
    && typeof value.d === "string",
  );
}

function assertHandoverVersion(version) {
  const numeric = Number(version);
  if (!Number.isFinite(numeric) || numeric !== HANDOVER_VERSION) {
    throw createCoreError("core.errors.unsupportedHandoverVersion", { version });
  }
}

/**
 * Build a versioned handover envelope for QR / deep links.
 * Payload is project-only (no layout).
 *
 * @param {object} project
 * @param {{ codec?: string }} [options]
 */
export function encodeHandoverProject(project, options = {}) {
  const codecId = options.codec || DEFAULT_HANDOVER_CODEC;
  const codec = getHandoverCodec(codecId);

  return {
    kind: HANDOVER_KIND,
    version: HANDOVER_VERSION,
    codec: codecId,
    d: codec.encode(project),
  };
}

/**
 * @param {object} envelope
 * @returns {object} normalized project
 */
export function decodeHandoverEnvelope(envelope) {
  if (!isHandoverEnvelope(envelope)) {
    throw createCoreError("core.errors.invalidHandover");
  }

  assertHandoverVersion(envelope.version);
  const codec = getHandoverCodec(envelope.codec);
  const project = codec.decode(envelope.d);
  return normalizeProject(project);
}

/**
 * Parse `v`, `c`, `d` query params or bundled `h` param.
 *
 * @param {Record<string, string | string[] | undefined>} params
 */
export function parseHandoverSearchParams(params) {
  const bundled = firstParam(params.h);
  if (bundled) {
    const json = new TextDecoder().decode(decodeBase64Url(bundled));
    const envelope = JSON.parse(json);
    if (!isHandoverEnvelope(envelope)) {
      throw createCoreError("core.errors.invalidHandover");
    }
    return envelope;
  }

  const payload = firstParam(params.d);
  const codecId = firstParam(params.c) || DEFAULT_HANDOVER_CODEC;
  const version = firstParam(params.v) || String(HANDOVER_VERSION);

  if (!payload) {
    throw createCoreError("core.errors.missingHandoverPayload");
  }

  assertHandoverVersion(version);

  return {
    kind: HANDOVER_KIND,
    version: HANDOVER_VERSION,
    codec: codecId,
    d: payload,
  };
}

/**
 * @param {string} url
 */
export function parseHandoverUrl(url) {
  if (!url || typeof url !== "string") {
    throw createCoreError("core.errors.invalidHandover");
  }

  const normalized = url.trim();
  const queryIndex = normalized.indexOf("?");
  const query = queryIndex >= 0 ? normalized.slice(queryIndex + 1) : "";
  const params = Object.fromEntries(new URLSearchParams(query));
  return parseHandoverSearchParams(params);
}

/**
 * @param {object} envelope
 * @param {{ scheme?: string, path?: string }} [options]
 */
export function buildHandoverDeepLink(envelope, options = {}) {
  if (!isHandoverEnvelope(envelope)) {
    throw createCoreError("core.errors.invalidHandover");
  }

  const scheme = options.scheme || DEFAULT_HANDOVER_SCHEME;
  const path = options.path || HANDOVER_IMPORT_PATH;
  const search = new URLSearchParams({
    v: String(envelope.version),
    c: envelope.codec,
    d: envelope.d,
  });

  return `${scheme}://${path}?${search.toString()}`;
}

function firstParam(value) {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}
